// src/FilingResults.js
import React, { useState } from 'react';

function StatusIcon({ status }) {
  if (status === 'confirmed') {
    return (
      <div className="fr-status-icon fr-status-icon--confirmed">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  }
  if (status === 'faxed') {
    // Faxed but not yet confirmed — pulse ring to show in-progress
    return (
      <div className="fr-status-icon fr-status-icon--faxed">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6
            19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72
            12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91
            a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45
            12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  }
  if (status === 'sending') {
    return (
      <div className="fr-status-icon fr-status-icon--sending">
        <div className="fp-spinner-lg" style={{ width: 22, height: 22 }} />
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="fr-status-icon fr-status-icon--failed">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
      </div>
    );
  }
  // queued / null
  return (
    <div className="fr-status-icon fr-status-icon--queued">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </div>
  );
}

function TransactionTable({ transactions }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? transactions : transactions.slice(0, 6);
  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="fr-tx-table-wrap">
      <table className="fr-tx-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((t, i) => (
            <tr key={t.id || i}>
              <td className="fr-tx-date">{t.date || '—'}</td>
              <td className="fr-tx-desc">{t.description}</td>
              <td className="fr-tx-amount">${fmt(t.amount || 0)}</td>
              <td>
                <span className={`fr-tx-badge ${t.direction === 'paid_to_llc' ? 'out' : 'in'}`}>
                  {t.direction === 'paid_to_llc' ? '↑ To LLC' : '↓ From LLC'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {transactions.length > 6 && (
        <button className="fr-expand-btn" onClick={() => setExpanded(!expanded)} type="button">
          {expanded ? 'Show less' : `Show all ${transactions.length} transactions`}
        </button>
      )}
    </div>
  );
}

export function FilingResults({
  filingData,
  transactions,
  faxStatus,
  faxConfirmation,
  generatedPdfBase64,
  onDownloadPDF,
  onStartOver,
}) {
  const isConfirmed = faxStatus === 'confirmed';
  // NEW: 'faxed' = queued/in-progress, shown as soon as faxId is received
  const isFaxed     = faxStatus === 'faxed';
  const isFailed    = faxStatus === 'failed';
  const isSending   = faxStatus === 'sending';
  const isQueued    = !faxStatus;

  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalPaid = (transactions || [])
    .filter(t => t.direction === 'paid_to_llc')
    .reduce((s, t) => s + (t.amount || 0), 0);
  const totalReceived = (transactions || [])
    .filter(t => t.direction === 'received_from_llc')
    .reduce((s, t) => s + (t.amount || 0), 0);

  const confirmedAt = faxConfirmation?.confirmedAt
    ? new Date(faxConfirmation.confirmedAt).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  const queuedAt = faxConfirmation?.queuedAt
    ? new Date(faxConfirmation.queuedAt).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  const statusClass = isConfirmed ? 'confirmed'
    : isFaxed   ? 'faxed'
    : isFailed  ? 'failed'
    : isSending ? 'sending'
    : 'queued';

  return (
    <div className="fr-root">

      {/* Top bar */}
      <div className="fr-topbar">
        <div className="fr-topbar-left">
          <div className="fr-doc-pill">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Form 5472 — {filingData?.llcName} — {filingData?.taxYear}
          </div>
        </div>
        <div className="fr-topbar-right">
          {generatedPdfBase64 && (
            <button className="fp-btn-ghost" onClick={onDownloadPDF} type="button">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download PDF
            </button>
          )}
          <button className="fr-new-btn" onClick={onStartOver} type="button">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            New Filing
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="fr-body">

        {/* LEFT panel */}
        <div className="fr-left">

          {/* Status card */}
          <div className={`fr-status-card fr-status-card--${statusClass}`}>
            <StatusIcon status={faxStatus} />
            <div className="fr-status-text">
              <div className="fr-status-headline">
                {isConfirmed && 'Delivered to the IRS'}
                {isFaxed     && 'Faxed — awaiting delivery confirmation'}
                {isSending   && 'Sending fax to IRS…'}
                {isFailed    && 'Fax delivery failed'}
                {isQueued    && 'Fax queued — sending to IRS'}
              </div>
              <div className="fr-status-sub">
                {isConfirmed && confirmedAt  && `Delivered ${confirmedAt}`}
                {isFaxed     && queuedAt     && `Faxed at ${queuedAt} · Delivery typically within 2–10 min`}
                {isFaxed     && !queuedAt    && 'Delivery typically within 2–10 minutes'}
                {isSending   && 'Do not close this tab.'}
                {isFailed    && (faxConfirmation?.failureReason || 'Please try again — the IRS line may have been busy.')}
                {isQueued    && 'Typically delivers within 2–10 minutes.'}
              </div>
            </div>
          </div>

          {/* Fax confirmation — shown as soon as faxId is available */}
          {faxConfirmation && (
            <div className="fr-card">
              <div className="fr-card-title">
                {isConfirmed ? 'Delivery Confirmation' : 'Fax Receipt'}
              </div>
              <div className="fr-detail-list">
                {[
                  ['Fax ID',    faxConfirmation.faxId],
                  ['Sent to',   faxConfirmation.sentTo   || 'IRS Fax Line'],
                  ['Pages',     faxConfirmation.pages    || 4],
                  ['Status',    isConfirmed ? 'Delivered' : isFaxed ? 'Faxed — in transit' : faxConfirmation.status],
                   isConfirmed && confirmedAt
                    ? ['Delivered', confirmedAt]
                    : queuedAt
                    ? ['Faxed at',  queuedAt]
                    : null,
                  isFailed && faxConfirmation.failureReason
                    ? ['Failure reason', faxConfirmation.failureReason]
                    : null,
                ].filter(Boolean).map(([label, value]) => (
                  <div className="fr-detail-row" key={label}>
                    <span className="fr-detail-label">{label}</span>
                    <span className="fr-detail-value">{value}</span>
                  </div>
                ))}
              </div>
              {/* Subtle note while waiting for delivery */}
              {isFaxed && (
                <p className="fr-note">
                  Your fax is in transit. The Fax ID above is your proof of submission — save it for your records even before delivery is confirmed.
                </p>
              )}
              {faxConfirmation.note && isConfirmed && (
                <p className="fr-note">{faxConfirmation.note}</p>
              )}
            </div>
          )}

          {/* Filing details */}
          <div className="fr-card">
            <div className="fr-card-title">Filing Details</div>
            <div className="fr-detail-list">
              {[
                ['LLC',         filingData?.llcName],
                ['EIN',         filingData?.ein],
                ['Tax Year',    filingData?.taxYear],
                ['Owner',       filingData?.ownerName],
                ['Country',     filingData?.ownerCountry],
                ['Paid to LLC', `$${fmt(totalPaid)}`],
                ['From LLC',    `$${fmt(totalReceived)}`],
              ].map(([label, value]) => (
                <div className="fr-detail-row" key={label}>
                  <span className="fr-detail-label">{label}</span>
                  <span className="fr-detail-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="fr-actions">
            {generatedPdfBase64 && (
              <button className="fr-btn-primary" onClick={onDownloadPDF} type="button">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download Form 5472 PDF
              </button>
            )}
            <button className="fr-btn-secondary" onClick={onStartOver} type="button">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              File another year
            </button>
          </div>

          {/* Keep for records */}
          <div className="fr-card">
            <div className="fr-card-title">Keep for Your Records</div>
            <div className="fr-keep-list">
              {[
                faxConfirmation?.faxId
                  ? `Fax ID: ${faxConfirmation.faxId}`
                  : 'Screenshot this page or save the Fax ID',
                'Your downloaded Form 5472 PDF',
                'Your original bank statement',
                'The fax confirmation number above',
              ].map((item) => (
                <div className="fr-keep-item" key={item}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* RIGHT panel */}
        <div className="fr-right">

          {/* Penalty callout */}
          <div className="fr-penalty-card">
            <div className="fr-penalty-shield">🛡️</div>
            <div>
              <div className="fr-penalty-headline">$25,000 penalty avoided</div>
              <div className="fr-penalty-sub">
                Foreign-owned LLCs that miss Form 5472 face a $25,000 IRS penalty per year.
                You're covered for {filingData?.taxYear}.
              </div>
            </div>
          </div>

          {/* Transaction table */}
          {transactions?.length > 0 && (
            <div className="fr-card">
              <div className="fr-card-title">Reported Transactions ({transactions.length})</div>
              <TransactionTable transactions={transactions} />
            </div>
          )}

          {/* Next steps */}
          <div className="fr-card">
            <div className="fr-card-title">Next Steps</div>
            <div className="fr-next-list">
              {[
                {
                  n: '1',
                  title: 'Download your PDF',
                  text: 'Save Form 5472 with your tax records.',
                },
                {
                  n: '2',
                  title: 'State annual report',
                  text: "File your LLC's annual report with your state — this is separate from this IRS filing.",
                },
                {
                  n: '3',
                  title: 'Set a reminder',
                  text: `Come back next year — set a reminder for March ${parseInt(filingData?.taxYear || '2024') + 1}.`,
                },
              ].map(({ n, title, text }) => (
                <div className="fr-next-item" key={n}>
                  <div className="fr-next-num">{n}</div>
                  <div>
                    <div className="fr-next-title">{title}</div>
                    <div className="fr-next-text">{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}